import { Check, Building2, Linkedin, ExternalLink } from "lucide-react";

const formatRevenue = (r) => {
  if (!r) return "—";
  if (r >= 1_000_000) return `$${(r / 1_000_000).toFixed(1)}M`;
  if (r >= 1_000) return `$${(r / 1_000).toFixed(0)}k`;
  return `$${r}`;
};

/** Apollo results table + master-select header row. Pure presentation — no fetch logic. */
export function ApolloResultsTable({ results, selected, onToggle, onToggleAll }) {
  const eligible = results.filter((r) => !r.already_imported);
  const allChecked = eligible.length > 0 && eligible.every((r) => selected[r.id]);
  return (
    <div data-testid="apollo-results-table" className="bg-white border border-neutral-200 overflow-x-auto">
      <table className="lf-grid">
        <thead>
          <tr>
            <th style={{ width: 40 }}>
              <input
                type="checkbox"
                data-testid="apollo-select-all"
                onChange={onToggleAll}
                checked={allChecked}
              />
            </th>
            <th>Business</th>
            <th>Industry</th>
            <th>Location</th>
            <th>Size</th>
            <th>Revenue</th>
            <th>Phone</th>
            <th>Website</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.id} data-testid={`apollo-row-${r.id}`}>
              <td>
                <input
                  type="checkbox"
                  checked={!!selected[r.id]}
                  disabled={r.already_imported}
                  onChange={() => onToggle(r.id)}
                  data-testid={`apollo-select-${r.id}`}
                />
              </td>
              <td>
                <div className="font-semibold flex items-center gap-1.5">
                  {r.logo_url ? (
                    <img src={r.logo_url} alt="" className="w-4 h-4 object-contain rounded-sm" />
                  ) : (
                    <Building2 className="w-3.5 h-3.5 text-neutral-400" />
                  )}
                  {r.name}
                  {r.linkedin_url && (
                    <a href={r.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-800 inline-flex items-center">
                      <Linkedin className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {r.contact_name && (
                  <div className="text-xs text-orange-700 mt-0.5 font-medium">
                    👤 {r.contact_name}{r.title ? ` · ${r.title}` : ""}
                  </div>
                )}
                {r.email && (
                  <div className="text-[11px] text-neutral-500 mt-0.5 font-mono">✉ {r.email}</div>
                )}
                {r.founded_year && !r.contact_name && (
                  <div className="text-[10px] text-neutral-400 mt-0.5">Founded {r.founded_year}</div>
                )}
              </td>
              <td className="text-xs text-neutral-600 max-w-[160px]">
                <div>{r.industry}</div>
                {r.keywords?.length > 0 && (
                  <div className="text-[10px] text-neutral-400 mt-0.5 truncate" title={r.keywords.join(", ")}>
                    {r.keywords.slice(0, 3).join(", ")}
                  </div>
                )}
              </td>
              <td className="text-xs text-neutral-600">
                {[r.city, r.state, r.country].filter(Boolean).join(", ") || "—"}
              </td>
              <td className="text-xs font-mono">{r.employee_count || "—"}</td>
              <td className="text-xs font-mono">{r.revenue_printed || formatRevenue(r.revenue)}</td>
              <td className="font-mono text-xs">{r.phone || "—"}</td>
              <td>
                {r.website ? (
                  <a href={r.website} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-neutral-600 hover:text-black">
                    <ExternalLink className="w-3 h-3" /> yes
                  </a>
                ) : (
                  <span className="pill pill-interested text-[10px]">no website</span>
                )}
              </td>
              <td className="text-right">
                {r.already_imported ? (
                  <span className="pill pill-interested inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Imported
                  </span>
                ) : (
                  <span className="text-xs text-neutral-400">select →</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
